import { useEffect, useRef, useState, useCallback } from 'react';

interface UseVisibilityOptions {
  /**
   * The threshold in viewport heights above and below the viewport
   * where elements should be considered visible. Default is 1.5
   */
  viewportThreshold?: number;
  /**
   * Debounce time in milliseconds for visibility changes
   */
  debounceMs?: number;
}

/**
 * A hook that determines if an element is within a certain range of the viewport.
 * Elements within the threshold above or below the viewport will be considered visible.
 * This helps optimize performance by only mounting components when they're close to being visible.
 */
export const useVisibility = (options: UseVisibilityOptions = {}) => {
  const { viewportThreshold = 1.5, debounceMs = 100 } = options;
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);

  // Debounced setState to prevent rapid changes
  const debouncedSetVisible = useCallback((value: boolean) => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      setIsVisible(value);
      timeoutRef.current = null;
    }, debounceMs);
  }, [debounceMs]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Use the first entry since we're only observing one element
        const entry = entries[0];
        if (entry) {
          const elementTop = entry.boundingClientRect.top;
          const elementBottom = entry.boundingClientRect.bottom;
          const windowHeight = window.innerHeight;
          
          // Calculate the extended viewport range
          const extendedViewportTop = -windowHeight * viewportThreshold;
          const extendedViewportBottom = windowHeight * (1 + viewportThreshold);
          
          // Element is considered visible if it's within the extended viewport range
          const isInExtendedViewport = 
            elementTop < extendedViewportBottom && 
            elementBottom > extendedViewportTop;

          // Use debounced setter to prevent rapid changes
          debouncedSetVisible(isInExtendedViewport);
        }
      },
      {
        threshold: [0, 0.1, 0.5, 1.0],
        rootMargin: `${viewportThreshold * 100}% 0px ${viewportThreshold * 100}% 0px`
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      // Clear any pending timeout on cleanup
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [viewportThreshold, debouncedSetVisible]);

  return { elementRef, isVisible };
};
